import axios from "axios";
import { BASE_URL } from "../constants";

export const refreshAccessToken = async (refreshToken) => {
  try {
    const response = await axios.post(`${BASE_URL}/refresh-token`, {
      refreshToken,
    });
    
    if (response.status === 200) {
      return {
        status: 200,
        token: response.data.token,
        refreshToken: response.data.refreshToken,
      };
    }
    
    return { status: response.status, message: response.data?.message };
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      message: error?.response?.data?.message || "Token refresh failed",
      error: error?.response?.data?.error || error?.response?.data?.message || "Token refresh failed",
    };
  }
};

/**
 * Request password reset email
 * @param {string} email - User's email address
 * @returns {Promise<Object>} Response object with status and message
 */
export const forgotPassword = async (email) => {
  try {
    const response = await axios.post(`${BASE_URL}/forgot-password`, {
      email,
    });
    
    return {
      status: response.status,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      message: error?.response?.data?.message || "Failed to process request",
      error: error?.response?.data?.error || error?.response?.data?.message || "Failed to process request",
    };
  }
};

/**
 * Verify password reset token
 * @param {string} token - Reset token from URL
 * @returns {Promise<Object>} Response object with status, valid flag, and email
 */
export const verifyResetToken = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/verify-reset-token`, {
      params: { token },
    });
    
    return {
      status: response.status,
      valid: response.data?.valid,
      email: response.data?.email,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      valid: false,
      message: error?.response?.data?.message || "Failed to verify token",
      error: error?.response?.data?.error || error?.response?.data?.message || "Failed to verify token",
    };
  }
};

/**
 * Reset password using token
 * @param {string} token - Reset token
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response object with status and message
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await axios.post(`${BASE_URL}/reset-password`, {
      token,
      newPassword,
    });
    
    return {
      status: response.status,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      message: error?.response?.data?.message || "Failed to reset password",
      error: error?.response?.data?.error || error?.response?.data?.message || "Failed to reset password",
    };
  }
};

/**
 * Change password for authenticated user
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response object with status and message
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await axios.post(`${BASE_URL}/change-password`, {
      currentPassword,
      newPassword,
    });
    
    return {
      status: response.status,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      message: error?.response?.data?.message || "Failed to change password",
      error: error?.response?.data?.error || error?.response?.data?.message || "Failed to change password",
    };
  }
};

/**
 * Get all users (admin only)
 * @returns {Promise<Object>} Response object with status and users list
 */
export const getUsers = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/users`);
    
    return {
      status: response.status,
      data: response.data?.data || response.data?.users || [],
    };
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      message: error?.response?.data?.message || "Failed to fetch users",
      error: error?.response?.data?.error || error?.response?.data?.message || "Failed to fetch users",
      data: [],
    };
  }
};

/**
 * Admin reset password for a user
 * @param {number} userId - User ID
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response object with status and message
 */
export const adminResetPassword = async (userId, newPassword) => {
  try {
    const response = await axios.post(`${BASE_URL}/admin/reset-password`, {
      userId,
      newPassword,
    });
    
    return {
      status: response.status,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      message: error?.response?.data?.message || "Failed to reset password",
      error: error?.response?.data?.error || error?.response?.data?.message || "Failed to reset password",
    };
  }
};
